import React, { useState, forwardRef, useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import { CustomEventType, Dict, MediaType, ScreenSize, ThumbSize } from 'app/types'
import { getMediaSelector, getMediaLinkSelector, useAppSelector, usePreviewMedia } from 'app/hooks'
import { Spinner, Icon, IconButton, MediaIcon } from 'components'
import Player from '@vimeo/player'
import { getVimeoJson } from 'services/appServices'
import { getOptionString } from 'helpers/requestHandler'
import * as fnc from 'helpers/fnc'
import { logger } from 'helpers/logger'
import { icons } from 'app/constants'
import { LensFlareSystem } from '@babylonjs/core'

const defaultProps = {
    mediaTypeId: MediaType.Image,
    thumb: false,
    thumbSize: ThumbSize.Medium,
    cropped: false,
    fadeIn: false,
    fadeInDelay: 0,
    fadeInStyle: {},
    spinner: false,
    autoplay: false,
}

const HOVER_PADDING = 20
const CLICK_DRAG_THRESHOLD = 50

// Convert any YouTube URL format to an embeddable youtube.com/embed/ URL
function convertYouTubeToEmbed(link: string): string {
    let videoId = null

    // Handle youtube.com/shorts/VIDEO_ID format
    const shortsMatch = link.match(/youtube\.com\/shorts\/([^?&#]+)/)
    if (shortsMatch) {
        videoId = shortsMatch[1]
    }

    // Handle youtu.be/VIDEO_ID format
    if (!videoId) {
        const shortMatch = link.match(/youtu\.be\/([^?&#]+)/)
        if (shortMatch) {
            videoId = shortMatch[1]
        }
    }

    // Handle youtube.com/watch?v=VIDEO_ID format
    if (!videoId) {
        const watchMatch = link.match(/[?&]v=([^&#]+)/)
        if (watchMatch) {
            videoId = watchMatch[1]
        }
    }

    // Handle youtube.com/embed/VIDEO_ID format (already correct)
    if (!videoId) {
        const embedMatch = link.match(/youtube\.com\/embed\/([^?&#]+)/)
        if (embedMatch) {
            videoId = embedMatch[1]
        }
    }

    if (!videoId) {
        // Fallback: return the link as-is if we can't parse it
        return link
    }

    return `https://www.youtube.com/embed/${videoId}?rel=0`
}

function convertYouTubeToEmbedAutoplay(link: string): string {
    const base = convertYouTubeToEmbed(link)
    return base.includes('?') ? `${base}&autoplay=1` : `${base}?autoplay=1`
}

export function MediaHover({ position, name, url }) {
    const [opacity, setOpacity] = useState(0);
    const [isMax, setIsMax] = useState(false);

    useEffect(() => {
        setTimeout(() => setOpacity(1), 500);
    }, []);

    if (!url) return null;

    const hoverContent = (
        <div
            className="media-hover"
            style={{
                position: 'absolute',
                left: `${position.x}px`,
                top: `${position.y}px`,
                opacity,
                transform: isMax
                    ? `translate(calc(-100% - 20px), 0)`
                    : `translate(20px, ${(0 - 100 * position.y / window.innerHeight)}%)`
            }}
        >
            <img src={url} alt={name} style={{ maxWidth: '100%', maxHeight: '100%' }} />
            {name && <span>{name}</span>}
        </div>
    );

    return ReactDOM.createPortal(hoverContent, document.body);
    // return createPortal(hoverContent, document.body);
}

interface MediaProps {
    mediaId: string,
    thumb?: boolean,
    thumbSize?: ThumbSize,
    thumbMediaId?: string,
    cropped?: boolean,
    className?: string,
    style?: Dict,
    onClick?: () => void,
    onLoad?: () => void,
    fadeIn?: boolean,
    fadeInDelay?: number,
    fadeInStyle?: {},
    app: AppData,
    builder: BuilderData,
    organization: OrganizationData,
    fallback: boolean,
    hoverShow: boolean,
    url: string,
    debug: boolean,
    fit: string,
    autoplay: boolean,
    background: boolean,
    focus: boolean,
    orientation: string,
    hideMediaIcon?: boolean,
}

interface VideoControlsProps {
    iframe,
    elem,
    options,
    mobile,
}

interface VideoProgressBarProps {
    iframe,
}

function VideoProgressBar(props: VideoProgressBarProps) {
    const { iframe } = props
    const [progress, setProgress] = useState(0)
    const [duration, setDuration] = useState(0)
    const [player, setPlayer] = useState(null)
    const [hasStarted, setHasStarted] = useState(false)

    useEffect(() => {
        if (!iframe) {
            return
        }

        // Skip YouTube videos - they don't use Vimeo Player
        const iframeElement = iframe instanceof HTMLElement ? iframe : null
        const iframeSrc = iframeElement?.src || ''
        if (iframeSrc.includes('youtube') || iframeSrc.includes('youtu.be')) {
            return
        }

        try {
            const newPlayer = new Player(iframe)
            setPlayer(newPlayer)

            // Listen for first play to show progress bar
            newPlayer.on('play', () => {
                setHasStarted(true)
            })

            // Get duration once player is ready
            newPlayer.ready().then(() => {
                return newPlayer.getDuration()
            }).then((dur) => {
                setDuration(dur)
            }).catch((error) => {
                logger.error('Error getting video duration:', error)
            })

        } catch (error) {
            logger.error('Error creating Vimeo player for progress bar:', error)
        }
    }, [iframe])

    useEffect(() => {
        if (!player || !duration || !hasStarted) {
            return
        }

        // Update progress every 100ms for smooth animation
        const interval = setInterval(() => {
            player.getCurrentTime().then((time) => {
                setProgress(time / duration)
            }).catch(() => {
                // Ignore errors if player is not ready
            })
        }, 100)

        return () => {
            clearInterval(interval)
        }
    }, [player, duration, hasStarted])

    // Don't render if video hasn't started yet
    if (!iframe || !player || duration === 0 || !hasStarted) {
        return null
    }

    const progressPercentage = Math.min(Math.max(progress * 100, 0), 100)

    return (
        <div className="video-progress-bar">
            <div
                className="video-progress-fill"
                style={{ width: `${progressPercentage}%` }}
            />
        </div>
    )
}

function VideoControlsMinimal(props: VideoControlsProps) {
    const { iframe, elem, options, mobile } = props
    const [player, setPlayer] = useState(null)
    const [playing, setPlaying] = useState(false)

    useEffect(() => {
        if (!iframe) {
            return
        }

        // Check if this is a YouTube video by checking the src attribute
        const iframeElement = iframe instanceof HTMLElement ? iframe : null
        const iframeSrc = iframeElement?.src || ''
        if (iframeSrc.includes('youtube') || iframeSrc.includes('youtu.be')) {
            return
        }

        try {
            const newPlayer = new Player(iframe)
            setPlayer(newPlayer)

            newPlayer.on('play', () => {
                setPlaying(true)
            })

            newPlayer.on('pause', () => {
                setPlaying(false)
            })

            newPlayer.on('ended', () => {
                setPlaying(false)
            })

            newPlayer.ready().then(() => {
                return newPlayer.getPaused()
            }).then((isPaused) => {
                setPlaying(!isPaused)
            }).catch((error) => {
                logger.error('Error getting initial video state:', error)
            })

        } catch (error) {
            logger.error('Error creating Vimeo player:', error)
        }
    }, [iframe])

    const handlePlayPause = () => {
        if (!player) {
            return
        }

        player.getPaused().then((isPaused) => {
            if (isPaused) {
                player.play()
            } else {
                player.pause()
            }
        }).catch((error) => {
            logger.error('Error toggling play/pause:', error)
        })
    }

    return (
        <div className="video-controls">
            <button onClick={handlePlayPause}>
                {playing ? 'Pause' : 'Play'}
            </button>
        </div>
    )
}

function VideoControls(props: VideoControlsProps) {
    const { iframe, elem, options, mobile } = props
    const [paused, setPaused] = useState(!options.autoplay)
    const [muted, setMuted] = useState(options.muted)
    const [seek, setSeek] = useState(0)
    const [hoverSeek, setHoverSeek] = useState(0)
    const [seeking, setSeeking] = useState(false)
    const [hovering, setHovering] = useState(false)
    const [player, setPlayer] = useState(null)
    const [duration, setDuration] = useState(0)
    const [clickStart, setClickStart] = useState([0, 0])
    const pausedProxy = useRef(paused)
    const seekRef = useRef()

    // Its possible if the play button is pressed to soon the play action doesn't work
    // This function attempts to play the player, but then checks it it truly is playing after a short interval
    // If not, it will try to play it again, repeat until pausedProxy == player.getPaused()
    function handlePlay() {
        if (!player) {
            return
        }

        // Try playing muted first on mobile to avoid browser restrictions
        if (mobile) {
            player.setVolume(0)
                .then(() => {
                    return player.play()
                })
                .then(() => {
                    // If we successfully played muted, try unmuting if user hasn't explicitly muted
                    if (!muted) {
                        setTimeout(() => {
                            player.setVolume(1)
                        }, 300)
                    }

                    if (pausedProxy.current) {
                        return
                    }

                    player.getPaused().then((p) => {
                        if (p) {
                            setTimeout(handlePlay, 100)
                        } else {
                            // We are playing
                        }
                    })
                })
                .catch((e) => {
                    // Fall back to regular play attempt if the muted approach fails
                    regularPlayAttempt()
                })
        } else {
            // For non-mobile, use the regular play approach
            regularPlayAttempt()
        }

        function regularPlayAttempt() {
            player.play().then(() => {
                if (pausedProxy.current) {
                    return
                }
                player.getPaused().then((p) => {
                    if (p) {
                        setTimeout(handlePlay, 100)
                    } else {
                        // We are playing
                    }
                })
            }).catch((e) => {
                if (e.name === 'NotAllowedError') {
                    // Show a visual indicator that user needs to interact again
                    logger.info('Video playback requires user interaction - try tapping the play button again')
                } else {
                    setTimeout(handlePlay, 100)
                    logger.error('Error playing video', e)
                }
            })
        }
    }

    useEffect(() => {
        if (!iframe) {
            return
        }

        // Check if this is a YouTube video by checking the src attribute
        const iframeElement = iframe instanceof HTMLElement ? iframe : null
        const iframeSrc = iframeElement?.src || ''
        if (iframeSrc.includes('youtube') || iframeSrc.includes('youtu.be')) {
            return
        }

        try {
            // Pause iframe on window event
            const newPlayer = new Player(iframe, options)
            function handlePause() {
                setTimeout(() => {
                    setPaused(true)
                }, 100)
                setPaused(true)
            }
            setPlayer(newPlayer)

            newPlayer.getDuration().then(setDuration)
            window.addEventListener(CustomEventType.PauseVideo, handlePause)
            return () => {
                window.removeEventListener(CustomEventType.PauseVideo, handlePause)
            }
        } catch (error) {
            logger.error('Error creating Vimeo player in VideoControls:', error)
        }
    }, [iframe])

    useEffect(() => {
        if (!player) {
            return
        }
        const interval = setInterval(() => {
            player.getCurrentTime().then((time) => {
                setSeek(time / duration)
            })
        }, 100)
        return () => {
            clearInterval(interval)
        }
    }, [player, seek])

    useEffect(() => {
        if (!player) {
            return
        }
        if (seeking) {
            function clearSeek() {
                setSeeking(false)
            }
            function clearSeekTouch() {
                setSeeking(false)
            }
            window.addEventListener('mouseup', clearSeek)
            window.addEventListener('touchend', clearSeekTouch)
            window.addEventListener('touchcancel', clearSeekTouch)
            return () => {
                window.removeEventListener('mouseup', clearSeek)
                window.removeEventListener('touchend', clearSeekTouch)
                window.removeEventListener('touchcancel', clearSeekTouch)
            }
        }

    }, [player, seeking])

    useEffect(() => {
        pausedProxy.current = paused
        if (!player) {
            return
        }
        player.getPaused().then((playerPaused) => {
            if (paused && !playerPaused) {
                player.pause()
            } else if (!paused && playerPaused) {
                // handlePlay()
            }
        })
    }, [player, paused])

    useEffect(() => {
        if (!player) {
            return
        }
        player.getVolume().then((playerVolume) => {
            if (!muted && playerVolume != 1) {
                player.setVolume(1)
            } else if (muted && playerVolume != 0) {
                player.setVolume(0)
            }
        })
    }, [player, muted])


    function togglePaused(e, swipeAware = false) {
        e.stopPropagation()
        if (swipeAware) {
            const coords = getEventCoordinates(e)
            const dif = Math.sqrt(Math.pow(coords.pageX - clickStart[0], 2) + Math.pow(coords.pageY - clickStart[1], 2))
            if (dif < 20) {
                if (paused) {
                    handlePlay()
                }
                setPaused(!paused)
            }
        } else {
            if (paused) {
                handlePlay()
            }
            setPaused(!paused)
        }
    }

    function toggleFullscreen() {
        fnc.toggleFullscreen(elem)
    }

    function toggleVolume(e) {
        e.stopPropagation()
        setMuted(!muted)
    }
    // Enhanced coordinate detection for better mobile support
    function getEventCoordinates(e) {
        // For touch events, use clientX/Y and convert to pageX/Y
        if (e.touches && e.touches.length > 0) {
            const touch = e.touches[0]
            return {
                pageX: touch.clientX + (window.pageXOffset || document.documentElement.scrollLeft),
                pageY: touch.clientY + (window.pageYOffset || document.documentElement.scrollTop),
                clientX: touch.clientX,
                clientY: touch.clientY
            }
        }
        // For mouse events
        return {
            pageX: e.pageX,
            pageY: e.pageY,
            clientX: e.clientX,
            clientY: e.clientY
        }
    }

    function calcSeek(e) {
        const rect = seekRef.current.getBoundingClientRect()
        const coords = getEventCoordinates(e)

        // Get viewport scale factor for mobile devices
        const visualViewport = window.visualViewport
        const visualScale = visualViewport ? visualViewport.scale : 1

        // Get CSS zoom factor only for mobile Apple devices
        const isAppleDevice = /iPhone|iPad|iPod|iOS|Mac OS/i.test(navigator.userAgent)
        const cssZoom = (mobile && isAppleDevice) ? fnc.getAppZoom() : 1

        // Account for page scroll offset
        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop

        // Calculate client coordinates relative to viewport
        const clientX = coords.pageX - scrollLeft
        const clientY = coords.pageY - scrollTop

        // Adjust for both visual viewport scale and CSS zoom
        const adjustedClientX = visualViewport ? (clientX / visualScale) / cssZoom : clientX / cssZoom

        // Account for 17.5px margins on each side (35px total)
        const SEEK_MARGIN = 17.5
        const relativeX = adjustedClientX - rect.left - SEEK_MARGIN
        const usableWidth = rect.width - (SEEK_MARGIN * 2)
        const percentage = relativeX / usableWidth

        // Clamp the result to 0-1 range
        return Math.max(0, Math.min(1, percentage))
    }

    function handleSeekStart(e) {
        e.stopPropagation()
        e.preventDefault() // Prevent scrolling on mobile
        if (!player || !seekRef.current) {
            return
        }
        setSeeking(true)
        const seekPercentage = calcSeek(e)
        setSeek(seekPercentage)
        // Apply zoom correction to the time calculation as well
        const zoomValue = fnc.getAppZoom()
        const adjustedTime = duration * seekPercentage
        player.setCurrentTime(adjustedTime)
    }

    function handleSeekMove(e) {
        if (!seekRef.current) {
            return
        }
        e.preventDefault() // Prevent scrolling on mobile
        const newSeekPercentage = Math.max(0, Math.min(1, calcSeek(e)))
        if (seeking) {
            setSeek(newSeekPercentage)
            const adjustedTime = duration * newSeekPercentage
            player.setCurrentTime(adjustedTime)
            setHoverSeek(newSeekPercentage)
        } else {
            setHoverSeek(newSeekPercentage)
        }
    }

    function disableMouseDown(e) {
        e.stopPropagation()
    }
    function handlePauseStart(e) {
        const coords = getEventCoordinates(e)
        setClickStart([coords.pageX, coords.pageY])
    }


    if (!iframe || !player) {
        return null
    }

    let seekDotStyle = {}
    let seekFrontStyle = {}
    if (seekRef.current) {
        // Use 35px total margin (17.5px on each side) instead of 40px
        const SEEK_MARGIN_TOTAL = 35
        seekFrontStyle = {
            width: `${(seekRef.current.clientWidth - SEEK_MARGIN_TOTAL) * seek}px`
        }
        seekDotStyle = {
            left: `${(seekRef.current.clientWidth - SEEK_MARGIN_TOTAL) * (hovering ? hoverSeek : seek) + 17.5}px`,
            transition: (hovering) ? 'none' : 'all linear 100ms',
        }
    }

    return <div className='video-media-controls'>
        {paused && <MediaIcon type={MediaType.Video} />}
        <div className="row video-media-pause" style={{ height: '100%' }} onClick={(e) => togglePaused(e, true)} onMouseDown={handlePauseStart} onTouchStart={handlePauseStart} />
        <div className="row video-media-options">
            <IconButton noBg icon={`${paused ? icons.play : icons.pause}`} onClick={togglePaused} onMouseDown={disableMouseDown} />
            <div
                className="seek"
                onMouseDown={handleSeekStart}
                onTouchStart={handleSeekStart}
                onMouseMove={handleSeekMove}
                onTouchMove={handleSeekMove}
                onMouseEnter={() => setHovering(true)}
                onMouseLeave={() => setHovering(false)}
                ref={seekRef}
            >
                <div className="row">
                    <div className='seek-back' />
                    <div className='seek-front' style={seekFrontStyle} />
                    <div className='seek-dot' style={seekDotStyle} />
                </div>
            </div>
            {mobile && <div style={{ display: 'flex', flexDirection: 'row' }} onMouseDown={disableMouseDown}>
                <IconButton noBg icon={`${muted ? icons.volumeMute : icons.volumeUp}`} onClick={toggleVolume} />
            </div>}
            {!mobile && <div style={{ display: 'flex', flexDirection: 'row' }} onMouseDown={disableMouseDown}>
                <IconButton noBg icon={`${muted ? icons.volumeMute : icons.volumeUp}`} onClick={toggleVolume} />
                <IconButton noBg icon={icons.expand} onClick={toggleFullscreen} />
            </div>}
        </div>
    </div >
}

export const Media = forwardRef((props: MediaProps, ref) => {
    const {
        mediaTypeId: A,
        mediaId: B,
        autoplay: C,
        thumb,
        thumbSize,
        thumbMediaId,
        cropped,
        className,
        style,
        onClick,
        onLoad,
        fadeIn,
        fadeInDelay,
        fadeInStyle,
        spinner,
        app,
        builder,
        organization,
        fallback,
        hoverShow,
        url: forceUrl,
        debug,
        fit,
        focus,
        orientation,
        ...otherProps

    } = { ...defaultProps, ...props }

    const getMedia = getMediaSelector()
    const getMediaLink = getMediaLinkSelector()
    const [loaded, setLoaded] = useState(0)
    const [url, setUrl] = useState(null)
    const [originalUrl, setOriginalUrl] = useState(null)
    const [usingFallback, setUsingFallback] = useState(false)
    const [hovering, setHovering] = useState(null)
    const [elem, setElem] = useState(null)
    const [iframe, setIframe] = useState(null)
    const [showThumbnail, setShowThumbnail] = useState(thumb)
    const [showBackgroundThumbnail, setShowBackgroundThumbnail] = useState(false)
    const [backgroundThumbnailUrl, setBackgroundThumbnailUrl] = useState(null)
    const [isBackgroundThumbnailFadingOut, setIsBackgroundThumbnailFadingOut] = useState(false)
    const [backgroundVideoPlaying, setBackgroundVideoPlaying] = useState(false)
    // Add state for inline video thumbnails
    const [showInlineThumbnail, setShowInlineThumbnail] = useState(false)
    const [inlineThumbnailUrl, setInlineThumbnailUrl] = useState(null)
    const [isInlineThumbnailFadingOut, setIsInlineThumbnailFadingOut] = useState(false)
    // Add state for non-clickable video play/pause
    const [inlineVideoPlaying, setInlineVideoPlaying] = useState(false)
    const screen = useAppSelector((state: RootState) => state.app.screen)
    const previewMedia = usePreviewMedia()
    const mouseStart = useRef(null)

    const [videoOptions, setVideoOptions] = useState({})

    const mobile = screen.isMobile

    // Add effect for inline video play/pause state tracking
    useEffect(() => {
        const isYouTube = url?.includes('youtube') || url?.includes('youtu.be') || media?.link?.includes('youtube') || media?.link?.includes('youtu.be')
        if (mediaTypeId === MediaType.Video && !onClick && !props.clickable && !props.background && iframe && url && !isYouTube && !url.includes('jpg')) {
            try {
                const player = new Player(iframe)

                // Listen for play/pause events to update state
                player.on('play', () => {
                    setInlineVideoPlaying(true)
                })

                player.on('pause', () => {
                    setInlineVideoPlaying(false)
                })

                player.on('ended', () => {
                    setInlineVideoPlaying(false)
                })

                // Initialize state based on current player state
                player.ready().then(() => {
                    return player.getPaused()
                }).then((isPaused) => {
                    setInlineVideoPlaying(!isPaused)
                }).catch((error) => {
                    logger.error('Error getting initial video state:', error)
                })

            } catch (error) {
                logger.error('Error creating Vimeo player for inline video:', error)
            }
        }
    }, [mediaTypeId, onClick, props.clickable, props.background, iframe, url])

    // Add effect to handle focus changes for videos
    useEffect(() => {
        const isYouTube = url?.includes('youtube') || url?.includes('youtu.be') || media?.link?.includes('youtube') || media?.link?.includes('youtu.be')
        if (mediaTypeId === MediaType.Video && iframe && url && !isYouTube && !url.includes('jpg')) {
            // Only create Vimeo Player for Vimeo videos (not YouTube or image placeholders)
            try {
                const player = new Player(iframe)

                if (!focus) {
                    // If this video loses focus, pause it
                    player.pause().catch(() => {
                        // Ignore errors if player is not ready
                    })
                }
            } catch (error) {
                // Log error but don't crash the component
                logger.error('Error creating Vimeo player for focus handling:', error)
            }
        }
    }, [focus, iframe, mediaTypeId, url])

    // Add effect for background video autoplay
    useEffect(() => {
        const isYouTube = url?.includes('youtube') || url?.includes('youtu.be') || media?.link?.includes('youtube') || media?.link?.includes('youtu.be')
        if (props.background && mediaTypeId === MediaType.Video && iframe && url && !isYouTube && !url.includes('jpg')) {
            try {
                const player = new Player(iframe)

                // Listen for play events to track when video actually starts
                player.on('play', () => {
                    setBackgroundVideoPlaying(true)
                })

                // Listen for pause/ended events in case video stops
                player.on('pause', () => {
                    setBackgroundVideoPlaying(false)
                })

                player.on('ended', () => {
                    setBackgroundVideoPlaying(false)
                })

                // Wait for player to be ready, then start playback
                player.ready().then(() => {
                    // Ensure video is muted for autoplay compliance
                    return player.setVolume(0)
                }).then(() => {
                    // Start playback
                    return player.play()
                }).then(() => {
                    logger.info('Background video autoplay started successfully')
                    // setBackgroundVideoPlaying will be set by the 'play' event listener
                }).catch((error) => {
                    logger.error('Background video autoplay failed:', error)
                    // If autoplay fails, the thumbnail will remain visible
                    setBackgroundVideoPlaying(false)
                })
            } catch (error) {
                logger.error('Error creating Vimeo player for background video:', error)
                setBackgroundVideoPlaying(false)
            }
        }
    }, [props.background, iframe, mediaTypeId, url])

    // Fade out background thumbnail when video is playing
    useEffect(() => {
        if (showBackgroundThumbnail && backgroundVideoPlaying && props.background) {
            // Start fade out process
            setTimeout(() => {
                setIsBackgroundThumbnailFadingOut(true)
                setShowBackgroundThumbnail(false)

                // Remove the overlay completely after fade transition completes
                setTimeout(() => {
                    setIsBackgroundThumbnailFadingOut(false)
                }, 800) // Match the CSS transition duration
            }, 500)
        }
    }, [backgroundVideoPlaying, props.background, showBackgroundThumbnail])

    // Fade out inline thumbnail when video is playing
    useEffect(() => {
        if (showInlineThumbnail && inlineVideoPlaying) {
            // Start fade out process
            setTimeout(() => {
                setIsInlineThumbnailFadingOut(true)
                setShowInlineThumbnail(false)

                // Remove the overlay completely after fade transition completes
                setTimeout(() => {
                    setIsInlineThumbnailFadingOut(false)
                }, 800) // Match the CSS transition duration
            }, 100) // Faster fade for inline videos
        }
    }, [inlineVideoPlaying, showInlineThumbnail])

    let mediaTypeId = MediaType.Image
    let mediaId = props.mediaId
    let media = null
    let name = null
    let altText = null
    if (mediaId) {
        media = getMedia(mediaId)
        if (media) {
            mediaTypeId = media.mediaTypeId
        }
        if (mediaTypeId == MediaType.Tour || mediaTypeId == MediaType.Spin) {
            media = previewMedia(app || organization, media)
        }
        if (media) {
            name = media.name
            altText = media.altText || null
            mediaId = media.id
        } else {
            logger.info("Media not found", mediaId, props)
        }
    }

    useEffect(() => {
        setShowThumbnail(thumb)
    }, [thumb])

    // Initialize background thumbnail for background videos
    useEffect(() => {
        if (props.background && thumbMediaId && mediaTypeId === MediaType.Video) {
            setShowBackgroundThumbnail(true)
            const thumbnailUrl = getMediaLink(thumbMediaId, {
                thumb: true,
                thumbSize: ThumbSize.Large,
                app,
                builder,
                organization,
                mediaTypeId: MediaType.Image
            })
            setBackgroundThumbnailUrl(thumbnailUrl)
        } else {
            setShowBackgroundThumbnail(false)
            setBackgroundThumbnailUrl(null)
        }
    }, [props.background, thumbMediaId, mediaTypeId, app, builder, organization])

    // Initialize inline/lightbox thumbnail overlays for videos.
    // For clickable videos, only show the configured custom thumbnail.
    useEffect(() => {
        const isInlineVideo = !onClick && !props.clickable && !props.background
        const isClickableVideo = mediaTypeId === MediaType.Video && (onClick || props.clickable) && !props.background
        let thumbnailUrl = null
        if (isInlineVideo && thumbMediaId && mediaTypeId === MediaType.Video) {
            thumbnailUrl = getMediaLink(thumbMediaId, {
                thumb: true,
                thumbSize: ThumbSize.Large,
                app,
                builder,
                organization,
                mediaTypeId: MediaType.Image
            })
        } else if (isClickableVideo && thumbMediaId) {
            thumbnailUrl = getMediaLink(thumbMediaId, {
                thumb: true,
                thumbSize: ThumbSize.Large,
                app,
                builder,
                organization,
                mediaTypeId: MediaType.Image
            })
        } else if (mediaId) {
            thumbnailUrl = getMediaLink(mediaId, {
                thumb: true,
                thumbSize: ThumbSize.Large,
                app,
                builder,
                organization,
                mediaTypeId: MediaType.Image
            })
        }
        setShowInlineThumbnail(thumbnailUrl != null)
        setInlineThumbnailUrl(thumbnailUrl)
    }, [onClick, props.clickable, props.background, thumbMediaId, mediaTypeId, mediaId, app, builder, organization])

    // Fade out background thumbnail when video is playing
    useEffect(() => {
        if (showBackgroundThumbnail && backgroundVideoPlaying && props.background) {
            // Start fade out process
            setTimeout(() => {
                setIsBackgroundThumbnailFadingOut(true)
                setShowBackgroundThumbnail(false)

                // Remove the overlay completely after fade transition completes
                setTimeout(() => {
                    setIsBackgroundThumbnailFadingOut(false)
                }, 800) // Match the CSS transition duration
            }, 500)
        }
    }, [backgroundVideoPlaying, props.background, showBackgroundThumbnail])

    // Set by url prop
    useEffect(() => {
        setLoaded(0)
        if (forceUrl != null) {
            setUrl(forceUrl)
            if (url == null) {
                setOriginalUrl(forceUrl)
            }
        }
    }, [forceUrl])

    useEffect(() => {
        if (!url) {
            return
        }

        if (mediaTypeId == MediaType.Video) {
            // For background videos, delay loaded state to allow thumbnail fade timing
            if (props.background && showBackgroundThumbnail) {
                setTimeout(() => {
                    setLoaded(2)
                }, 1000) // Give video time to start playing
            } else {
                setLoaded(2)
            }
        }
    }, [url, props.background, showBackgroundThumbnail])


    // Set by media prop
    useEffect(() => {
        if (!mediaId) {
            return
        }
        if (mediaTypeId == MediaType.Image || mediaTypeId == MediaType.EmailResource || (showThumbnail && thumb)) {
            let newUrl = getMediaLink(mediaId, {
                thumb,
                thumbSize,
                thumbMediaId,
                suffix: cropped ? '_cropped' : '',
                app,
                builder,
                organization,
                mediaTypeId,
            })
            if (url != newUrl) {
                setLoaded(0)
                setUrl(newUrl)
                if (url == null) {
                    setOriginalUrl(newUrl)
                }
            }
        } else {
            // muted: mobile,
            let newVideoOptions = {
                muted: props.muted || false,
                autoplay: focus,
                background: 1,
                // controls: 0,
                autopause: 1,
            }
            if (props.background) {
                newVideoOptions = {
                    muted: 1,
                    autoplay: 1,
                    background: 1,
                    autopause: 0,
                    // Add quality parameters for background videos
                    quality: 'hd1080p', // Request highest quality available
                    responsive: 0, // Disable responsive mode to maintain quality
                    dnt: 1, // Do not track for privacy
                }
            }
            // Add loop parameter for non-clickable videos (inline videos)
            const isInlineVideo = !onClick && !props.clickable && !props.background
            if (isInlineVideo) {
                newVideoOptions.loop = 1
            }

            let newUrl = null
            if (mediaTypeId == MediaType.Video) {
                const isYouTubeLink = media.link.includes('youtube') || media.link.includes('youtu.be')
                if (isYouTubeLink) {
                    // Convert YouTube URLs to embeddable format; autoplay only in lightbox/background
                    newUrl = (focus || props.background) ? convertYouTubeToEmbedAutoplay(media.link) : convertYouTubeToEmbed(media.link)
                } else {
                    if (!props.focus && !props.background) {
                        // newVideoOptions.muted = true
                        newVideoOptions.autoplay = !mobile
                    }
                    newUrl = `${media.link}${getOptionString({ ...newVideoOptions })}`
                }
            } else {
                newUrl = media.link
            }

            setUrl(newUrl)
            setOriginalUrl(url)
            setVideoOptions(newVideoOptions)
        }
    }, [mediaId, thumb, thumbSize, showThumbnail])


    if (!media && !forceUrl) {
        return null
    }

    function handleElem(x) {
        setElem(x)
        if (ref && typeof ref == 'function') {
            ref(x)
        } else if (ref && typeof ref == 'object' && ref.current) {
            ref.current = x
        }
    }

    function handleLoad(e) {
        if (fadeIn && loaded == 0) {
            // Slightly convoluted loading to ensure spinner
            if (spinner) {
                setTimeout(() => {
                    // Fade out spinner
                    setLoaded(1)
                    setTimeout(() => {
                        // Unmount spinner
                        setLoaded(2)
                    }, 200)
                }, fadeInDelay)
            } else {
                setTimeout(() => {
                    setLoaded(2)
                }, fadeInDelay)
            }
        } else {
            setLoaded(2)
        }

        if (onLoad) {
            onLoad(e, true)
        }
    }

    function handleError(e) {
        // logger.error("Error loading media", e)
        if (fallback && !usingFallback && mediaId) {
            setUsingFallback(true)
            const fullImage = getMediaLink(mediaId, { app })
            setUrl(fullImage)
        } else {
            const missingImage = getMediaLink('missing')
            setUrl(missingImage)
        }
    }

    function handleClick(e) {
        const current = { x: e.pageX, y: e.pageY }
        let distance = 0
        if (mouseStart.current) {
            distance = Math.sqrt(Math.pow(current.x - mouseStart.current.x, 2) + Math.pow(current.y - mouseStart.current.y, 2))
        }
        // Only propogate if not a significant drag
        if (distance > CLICK_DRAG_THRESHOLD) {
            return
        }

        // Handle non-clickable video toggle
        if (mediaTypeId === MediaType.Video && !onClick && !props.clickable && iframe) {
            try {
                const player = new Player(iframe)
                player.getPaused().then((isPaused) => {
                    if (isPaused) {
                        player.play()
                        setInlineVideoPlaying(true)
                    } else {
                        player.pause()
                        setInlineVideoPlaying(false)
                    }
                }).catch((error) => {
                    logger.error('Error toggling video playback:', error)
                })
            } catch (error) {
                logger.error('Error creating Vimeo player for toggle:', error)
            }
            return
        }

        if (onClick) {
            onClick(e)
        }

        if (mediaTypeId == MediaType.Video && showThumbnail && !onClick) {
            setShowThumbnail(false)
        }
    }

    function handleMouseStart(e) {
        mouseStart.current = { x: e.pageX, y: e.pageY }
    }

    function handleMouseDown(e) {
        setHovering(null)
    }

    function handleMouseOver(e) {
        setHovering({ x: e.pageX, y: e.pageY })
    }
    function handleMouseMove(e) {
        setHovering({ x: e.pageX, y: e.pageY })
    }
    function handleMouseLeave(e) {
        setHovering(null)
    }


    let finalStyle = style !== null ? style : {}
    let fadeClass = 'show'
    if (fadeIn) {
        fadeClass = `${loaded > 0 ? ' show' : ' hide'}`
        if (fadeInStyle) {
            finalStyle = { ...finalStyle, ...fadeInStyle }
        }
    }

    let mediaElement = null
    if (url) {
        if (mediaTypeId == MediaType.Image || showThumbnail) {
            if (showThumbnail && mediaTypeId != MediaType.Image && !finalStyle?.cursor) {
                finalStyle = { ...finalStyle, cursor: 'pointer' }
            }
            // Filter out invalid props
            const {
                clickable,
                dynamicContentId,
                galleryId,
                thumbnailMediaId,
                appId,
                builderId,
                organizationId,
                fileCount,
                mediaTagId,
                dateCreated,
                dateModified,
                ...validProps
            } = otherProps

            mediaElement = <img
                ref={handleElem}
                className={`media noselect${className ? ` ${className}` : ''} ${fadeClass}${fit != null ? ` fit-${fit}` : ''}${orientation ? ` orientation-${orientation}` : ''}${clickable ? ' clickable' : ''}`}
                data-type={mediaTypeId}
                data-url={originalUrl}
                data-id={mediaId}
                data-app={appId ? appId : 'none'}
                data-builder={builderId ? builderId : 'none'}
                data-org={organizationId ? organizationId : 'none'}
                draggable={false}
                alt={altText || name}
                src={url}
                style={finalStyle}
                onClick={handleClick}
                onLoad={handleLoad}
                onError={handleError}
                onMouseDown={hoverShow ? handleMouseDown : handleMouseStart}
                onMouseOver={hoverShow ? handleMouseOver : null}
                onMouseMove={hoverShow ? handleMouseMove : null}
                onMouseLeave={hoverShow ? handleMouseLeave : null}
                {...validProps} />
        } else if (mediaTypeId == MediaType.Video) {
            let style = {}
            // If window is not 16/9, make video full width
            const ratio = window.innerWidth / window.innerHeight
            if (!props.background) {
                if (ratio < 16 / 9) {
                    style.width = '100%'
                    style.height = 'auto'
                } else {
                    // Otherwise make it full height
                    style.width = 'auto'
                    style.height = '100%'
                }
            }
            const isYouTube = url.includes('youtube') || url.includes('youtu.be') || media?.link?.includes('youtube') || media?.link?.includes('youtu.be')
            if (isYouTube) {
                // YouTube videos - just render iframe directly, no Vimeo player
                mediaElement = <div className={`media${orientation ? ` orientation-${orientation}` : ''}`} data-type={mediaTypeId} ref={handleElem} onClick={handleClick} onMouseDown={otherProps.onMouseDown} style={style}>
                    <iframe src={url} allowFullScreen frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" />
                </div>
            } else if (!url.includes('jpg')) {
                const isClickable = onClick || props.clickable
                const isBackgroundVideo = props.background
                const isLightboxVideo = props.focus || (props.className && props.className.includes('slideshow'))
                const isNonClickableVideo = mediaTypeId === MediaType.Video && !isClickable && !isBackgroundVideo && !isLightboxVideo
                const isExternallyClickableVideo = mediaTypeId === MediaType.Video && !!onClick && !showThumbnail && !isBackgroundVideo && !isLightboxVideo
                const showInlineOverlay = (isNonClickableVideo || isExternallyClickableVideo) && (showInlineThumbnail || isInlineThumbnailFadingOut) && inlineThumbnailUrl
                const shouldShowVideoIcon = isNonClickableVideo && loaded === 2 && !inlineVideoPlaying

                // Let the wrapper receive clicks for inline-toggle and lightbox-open video modes.
                const iframeStyle = (isNonClickableVideo || isExternallyClickableVideo) ? { pointerEvents: 'none' } : {}

                mediaElement = <div className={`media${orientation ? ` orientation-${orientation}` : ''}${props.background ? ' background' : ''}`} data-type={mediaTypeId} ref={handleElem} onClick={isLightboxVideo ? null : handleClick} onMouseDown={otherProps.onMouseDown} style={style}>
                    {!props.background && isLightboxVideo && <VideoControls iframe={iframe} elem={elem} options={videoOptions} mobile={mobile} />}
                    {!props.background && isClickable && !isLightboxVideo && !onClick && <VideoControlsMinimal iframe={iframe} elem={elem} options={videoOptions} mobile={mobile} />}
                    {/* MediaIcon for non-clickable videos - positioned to intercept clicks */}
                    {shouldShowVideoIcon && <MediaIcon type={MediaType.Video} show={true} />}
                    <iframe ref={setIframe} src={url} frameBorder="0" allow="autoplay;fullscreen; picture-in-picture " allowFullScreen style={iframeStyle} />
                    {!props.background && (isClickable || isLightboxVideo) && <Spinner />}
                    {/* Background video thumbnail overlay */}
                    {props.background && (showBackgroundThumbnail || isBackgroundThumbnailFadingOut) && backgroundThumbnailUrl && (
                        <div
                            className={`background-thumbnail-overlay ${showBackgroundThumbnail ? 'fade-in' : 'fade-out'}`}
                            style={{ backgroundImage: `url(${backgroundThumbnailUrl})` }}
                        />
                    )}
                    {/* Inline video thumbnail overlay */}
                    {showInlineOverlay && (
                        <div
                            className={`inline-thumbnail-overlay ${showInlineThumbnail ? 'fade-in' : 'fade-out'}`}
                            style={{ 
                                backgroundImage: `url(${inlineThumbnailUrl})`,
                                pointerEvents: 'none'
                            }}
                        />
                    )}
                    {/* VideoProgressBar for non-clickable videos */}
                    {isNonClickableVideo && <VideoProgressBar iframe={iframe} />}
                </div>
            } else {
                return <Spinner />
            }
        }
    }

    // Show MediaIcon for thumbnails and clickable videos, but not for non-clickable videos (they have MediaIcon inside)
    const isNonClickableVideo = mediaTypeId === MediaType.Video && !onClick && !props.clickable && !props.background
    const showClickableVideoIcon = mediaTypeId === MediaType.Video && !!onClick && !showThumbnail && loaded == 2 && !props.hideMediaIcon
    const showIcon = (showThumbnail || showClickableVideoIcon) && loaded == 2 && mediaTypeId != MediaType.Image && !isNonClickableVideo && !props.hideMediaIcon

    if (spinner) {
        return <React.Fragment>
            {loaded < 2 && spinner && <Spinner show={loaded < 1} />}
            <MediaIcon type={mediaTypeId} show={showIcon} />
            {mediaElement}
        </React.Fragment>
    }
    return <React.Fragment>
        <MediaIcon type={mediaTypeId} show={showIcon} />
        {mediaElement}
        {(mediaId || url) && hoverShow && hovering && <MediaHover name={name} position={hovering} url={(url && !mediaId) ? url.split('?')[0] : getMediaLink(mediaId, { app, builder, organization, thumb: true, thumbSize: ThumbSize.ExtraLarge })} />}
    </React.Fragment>
})
